import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
  onConfirm: () => void;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  onConfirm,
  loading = false,
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading}
            className={buttonVariants({
              variant: variant === "destructive" ? "destructive" : "default",
            })}
          >
            {loading ? "Processing..." : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Hook for managing confirm dialog state
import { useState, useCallback } from "react";

interface UseConfirmDialogOptions {
  title: string;
  description: string;
  confirmText?: string;
  variant?: "default" | "destructive";
}

export function useConfirmDialog(options: UseConfirmDialogOptions) {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [loading, setLoading] = useState(false);

  const confirm = useCallback((action: () => void | Promise<void>) => {
    setPendingAction(() => action);
    setIsOpen(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (pendingAction) {
      setLoading(true);
      try {
        await pendingAction();
      } finally {
        setLoading(false);
        setIsOpen(false);
        setPendingAction(null);
      }
    }
  }, [pendingAction]);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open && !loading) {
      setIsOpen(false);
      setPendingAction(null);
    }
  }, [loading]);

  const dialogProps = {
    open: isOpen,
    onOpenChange: handleOpenChange,
    onConfirm: handleConfirm,
    loading,
    ...options,
  };

  return { confirm, dialogProps, ConfirmDialog };
}
